import { useEffect, useState } from "react";
import { Search, Users, LifeBuoy, Power, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { buildWhatsAppUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const TICKET_TYPES = {
  assistencia: "Assistência técnica",
  devolucao: "Devolução",
  garantia: "Garantia",
  arrependimento: "Direito de arrependimento",
  outro: "Outro assunto",
};
const TICKET_STATUS = {
  aberto: ["Aberto", "bg-amber-50 text-amber-700"],
  em_atendimento: ["Em atendimento", "bg-blue-50 text-brand"],
  resolvido: ["Resolvido", "bg-green-50 text-green-700"],
};

const CustomersTab = () => {
  const settings = useSettings();
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = (q = "") =>
    api.get(`/admin/customers${q ? `?search=${encodeURIComponent(q)}` : ""}`).then((r) => setCustomers(r.data)).catch(() => setCustomers([]));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (c) => {
    try {
      const { data } = await api.post(`/admin/customers/${c.id}/toggle-active`);
      setCustomers((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: data.active } : x)));
      toast.success(data.active ? "Cliente reativado." : "Cliente desativado.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/customers/${deleteTarget.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Dados pessoais do cliente removidos (LGPD).");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 max-w-md">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)}
          placeholder="Buscar por nome, e-mail ou telefone..."
          data-testid="customers-search"
        />
        <Button variant="outline" onClick={() => load(search)} data-testid="customers-search-btn">
          <Search className="w-4 h-4" />
        </Button>
      </div>
      {customers === null ? (
        <div className="animate-pulse h-40 bg-white rounded-2xl" />
      ) : customers.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-sm text-gray-500" data-testid="customers-empty">
          Nenhum cliente cadastrado.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="customers-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Contato</th>
                  <th className="p-4">Pedidos</th>
                  <th className="p-4">Cadastro</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50 last:border-0" data-testid={`customer-row-${c.id}`}>
                    <td className="p-4">
                      <p className="font-semibold text-gray-900">{c.name}</p>
                      <p className="text-xs text-gray-400">{c.cpf_masked || "—"}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-700">{c.email}</p>
                      <p className="text-xs text-gray-400">{c.phone}</p>
                    </td>
                    <td className="p-4 font-medium">{c.orders_count}</td>
                    <td className="p-4 text-xs text-gray-500">{fmtDateTime(c.created_at)}</td>
                    <td className="p-4">
                      <span className={`text-[10px] font-bold uppercase rounded-full px-2.5 py-0.5 ${c.active === false ? "bg-gray-100 text-gray-500" : "bg-green-50 text-green-700"}`}>
                        {c.active === false ? "Desativado" : "Ativo"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1 justify-end">
                        {c.phone && (
                          <a href={buildWhatsAppUrl("Olá! Aqui é da HG Móveis.", c.phone)} target="_blank" rel="noreferrer">
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" data-testid={`customer-wa-${c.id}`}>
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </a>
                        )}
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggle(c)} title={c.active === false ? "Reativar" : "Desativar"} data-testid={`customer-toggle-${c.id}`}>
                          <Power className={`w-4 h-4 ${c.active === false ? "text-green-600" : "text-gray-400"}`} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400 hover:text-red-600" onClick={() => setDeleteTarget(c)} title="Excluir dados (LGPD)" data-testid={`customer-delete-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="customer-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir dados do cliente?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-600">
            <p>
              Os dados pessoais de <b>{deleteTarget?.name}</b> serão anonimizados (LGPD). Os registros de pedidos são
              preservados para obrigações fiscais e legais. Essa ação não pode ser desfeita.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Voltar</Button>
              <Button onClick={doDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="customer-delete-confirm">
                {deleting ? "Excluindo..." : "Excluir dados"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SupportTab = () => {
  const settings = useSettings();
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [editTarget, setEditTarget] = useState(null);
  const [status, setStatus] = useState("em_atendimento");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/support").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/support/${editTarget.id}`, { status, note });
      toast.success("Solicitação atualizada.");
      setEditTarget(null);
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const visible = (tickets || []).filter((t) => filter === "todos" || t.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {["todos", "aberto", "em_atendimento", "resolvido"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${filter === s ? "bg-brand text-white" : "bg-white text-gray-600 border border-gray-200"}`}
            data-testid={`support-filter-${s}`}
          >
            {s === "todos" ? "Todos" : TICKET_STATUS[s][0]}
          </button>
        ))}
      </div>
      {tickets === null ? (
        <div className="animate-pulse h-40 bg-white rounded-2xl" />
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-sm text-gray-500" data-testid="support-empty">
          Nenhuma solicitação neste filtro.
        </div>
      ) : (
        <div className="space-y-3" data-testid="support-list">
          {visible.map((t) => {
            const st = TICKET_STATUS[t.status] || [t.status, "bg-gray-100 text-gray-600"];
            return (
              <div key={t.id} className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" data-testid={`support-ticket-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{TICKET_TYPES[t.type] || t.type}</span>
                      <span className={`text-[10px] font-bold uppercase rounded-full px-2.5 py-0.5 ${st[1]}`}>{st[0]}</span>
                      {t.order_number && <span className="text-xs text-gray-400">pedido {t.order_number}</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t.customer_name} • {t.customer_email}{t.customer_phone ? ` • ${t.customer_phone}` : ""} • {fmtDateTime(t.created_at)}
                    </p>
                    <p className="text-sm text-gray-700 mt-2">{t.message}</p>
                    {t.history?.length > 1 && (
                      <ul className="mt-2 space-y-1">
                        {t.history.slice(1).map((h, i) => (
                          <li key={i} className="text-xs text-gray-500">
                            {TICKET_STATUS[h.status]?.[0] || h.status} — {fmtDateTime(h.at)}{h.note ? `: ${h.note}` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    {t.customer_phone && (
                      <a href={buildWhatsAppUrl(`Olá ${t.customer_name}! Sobre sua solicitação${t.order_number ? ` do pedido ${t.order_number}` : ""}...`, t.customer_phone)} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" className="rounded-full text-green-600 border-green-200" data-testid={`support-wa-${t.id}`}>
                          <MessageCircle className="w-4 h-4" /> WhatsApp
                        </Button>
                      </a>
                    )}
                    <Button size="sm" className="rounded-full" onClick={() => { setEditTarget(t); setStatus(t.status); }} data-testid={`support-edit-${t.id}`}>
                      Atualizar
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="max-w-md" data-testid="support-edit-dialog">
          <DialogHeader>
            <DialogTitle>Atualizar solicitação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="support-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v[0]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota interna / retorno ao cliente (opcional)" data-testid="support-edit-note" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} data-testid="support-edit-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminCustomers() {
  const [tab, setTab] = useState("clientes");
  return (
    <div className="p-4 sm:p-8 max-w-6xl" data-testid="admin-customers-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-6">Clientes</h1>
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("clientes")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "clientes" ? "bg-brand text-white" : "bg-white text-gray-600 border border-gray-200"}`}
          data-testid="customers-tab-clientes"
        >
          <Users className="w-4 h-4" /> Clientes
        </button>
        <button
          onClick={() => setTab("assistencia")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === "assistencia" ? "bg-brand text-white" : "bg-white text-gray-600 border border-gray-200"}`}
          data-testid="customers-tab-assistencia"
        >
          <LifeBuoy className="w-4 h-4" /> Assistência
        </button>
      </div>
      {tab === "clientes" ? <CustomersTab /> : <SupportTab />}
    </div>
  );
}
